/* eslint-disable react-hooks/exhaustive-deps */
import i18n from 'i18n';
import React from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router';
import ApplicationState from 'store/interfaces';
import { modalOpen, modalClose } from 'modules/modal/actions';
import LockOpenIcon from '@material-ui/icons/LockOpen';
import LockIcon from '@material-ui/icons/Lock';
import { ModalFields } from 'modules/modal/interfaces';
import { PermissionsData, RolesEnum } from 'modules/authenticate/interfaces';
import {
  ListAllPartnerServersDataProps,
  PartnerFormFields,
  PartnerServersFields,
} from 'modules/partners/interfaces';
import TableItems, {
  MenuActionItem,
  MenuActions,
  TableCell,
  TableRow,
} from 'components/TableItems';
import {
  Box,
  Checkbox,
  CircularProgress,
  Grid,
  Tooltip,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { AlarmOff as AlarmOffIcon } from '@material-ui/icons';
import ButtonGroup from 'components/ButtonGroup';
import Permission from 'components/ValidPermission';

import {
  formatDateToISO,
  formatDateWithMoment,
  formatHourMinute,
} from 'utils/helpers';
import { getFlavorDisplayName } from 'utils/flavorDisplay';
import { isClientOperator } from 'utils/role';
import StatusInstance, {
  getStatusText,
  isFinalizingState,
} from 'containers/Environments/ListServers/status';
import Button from 'components/Button';
import {
  blockServerPartnerActionsRequest,
  getAllPartnerServersRequest,
  startPartnerServersRequest,
  stopPartnerServersRequest,
  unblockServerPartnerActionsRequest,
} from 'modules/partners/actions';
import { TableCellHeaderProps } from 'components/TableItems/interfaces';
import InfoIcon from '@material-ui/icons/InfoOutlined';
import RefreshButton from 'components/RefreshButton';
import { setFilterValues } from 'modules/filters/actions';
import {
  InstanceStateEnum,
  MenuActionItemProps,
} from 'modules/environments/interfaces';
import DynamicFilter from 'components/DynamicFilter';
import { FilterFields } from 'modules/filters/interfaces';
import { useTableFilters } from 'hooks/useTableFilters';
import { useServerActions } from 'containers/Environments/ListServers/useServerActions';
import FormNewScheduledStop from 'containers/Environments/ListServers/FormNewScheduledStop';
import ImagesLogs from 'containers/Environments/ListServers/PreparationServer/ImagesLogs';
import {
  detachServerRequest,
  getPublishHistoryRequest,
  stopTimeRequest,
} from 'modules/environments/actions';
import { DetachServerRequestProps } from 'modules/environments/requestInterfaces';
import OptimizedPublication from 'containers/Environments/Publications/OptimizedPublication';
import PublishHistory from 'components/PublishHistory';
import StatusInstanceServer, {
  getStatusText as getStatusTextServer,
} from 'containers/Environments/ListServers/statusServer';
import { Formik } from 'formik';
import GenericForm from 'skyone-lib/lib/GenericForm';
import palette from 'theme/palette';
import { Alert } from '@material-ui/lab';
import styles, {
  overrideOptimizedModal,
  overrideHistoryOptimizedModal,
  overrideModalBlockActions,
} from './styles';
import PartnersTemplate from '../PartnersTemplate';
import { filterOptions } from './dynamicFilterOptions';
import { blockSchema, unblockSchema } from './schema';

const useStyles = makeStyles(styles);

const requiredBulkActionsPermissions: Array<keyof PermissionsData> = [
  'publish_new_image_partner_servers',
  'start_partner_servers',
  'stop_partner_servers',
];

const requiredActionsPermissions: Array<keyof PermissionsData> = [
  'start_preparation_server',
  'stop_preparation_server',
  'publish_image',
  'detach_preparation_server',
];

const blockServerInitialValues = {
  block_actions_time_duration: 30,
  block_reason: '',
};

const unblockServerInitialValues = {
  unblock_reason: '',
};

interface Props {
  isLoading: boolean;
  permissions?: PermissionsData;
  role?: RolesEnum;
  allPartnerServersList: ListAllPartnerServersDataProps;
  filters: FilterFields[];
  isLoadingEnvironments: boolean;
  partner: PartnerFormFields | null;
  getAllPartnerServersRequest: (props) => void;
  startPartnerServersRequest: (props) => void;
  stopPartnerServersRequest: (props) => void;
  modalOpen: (props: ModalFields) => void;
  modalClose: () => void;
  detachServerRequest: (props: DetachServerRequestProps) => void;
  stopTimeRequest: ({
    serverId,
    environmentUuid,
    values,
    query,
    partnerUuid,
    server_type,
  }) => void;
  setFilterValues: (props: FilterFields[]) => void;
  getPublishHistoryRequest: ({ environmentUuid }) => void;
  blockServerPartnerActionsRequest: (props) => void;
  unblockServerPartnerActionsRequest: (props) => void;
}

const PartnerServers = ({
  isLoading,
  permissions,
  role,
  allPartnerServersList,
  filters,
  isLoadingEnvironments,
  partner,
  getAllPartnerServersRequest,
  startPartnerServersRequest,
  stopPartnerServersRequest,
  modalOpen,
  modalClose,
  detachServerRequest,
  stopTimeRequest,
  setFilterValues,
  getPublishHistoryRequest,
  blockServerPartnerActionsRequest,
  unblockServerPartnerActionsRequest,
}: Props) => {
  const { id } = useParams<{ id: string }>();
  const classes = useStyles();
  const [selectedAll, setSelectedAll] = React.useState(false);
  const [checks, setChecks] = React.useState<string[]>([]);
  const [refresh, setRefresh] = React.useState(true);
  const [stopRequest, setStopRequest] = React.useState(true);

  const {
    itemOrder,
    query,
    searchValue,
    handleOrder,
    handleLimitChange,
    handlePageChange,
    handleSearch,
    setSearchValue,
    setQuery,
  } = useTableFilters();

  const { handleStartServer, handleStopServer } = useServerActions(query);
  const intervalRef = React.useRef<any>();
  const RELOAD_TIME = 7500;

  const handleAllCheck = () => {
    if (!selectedAll) {
      const ids: string[] = [];
      allPartnerServersList.results.forEach(item =>
        ids.push(item?.instance_id)
      );
      setChecks(ids);
    } else {
      setChecks([]);
    }
    setSelectedAll(!selectedAll);
  };

  const handleCheck = (uuid: string) => {
    const findCheckId = checks.find(item => item === uuid);
    if (!findCheckId) {
      setChecks([...checks, uuid]);
    } else {
      const index = checks.indexOf(findCheckId);
      checks.splice(index, 1);
      setChecks([...checks]);
    }
  };

  const isActionsBlockedForOperator = (item: PartnerServersFields) =>
    item?.block_actions_enabled && !item?.can_execute_actions_on_blocked_server;

  const handleOptimizedHistoryPublish = (id: string) => {
    modalOpen({
      title: i18n.t('HISTORY_LOGS_PUBLISH_OPTIMIZED_KEY'),
      overrideClasses: overrideHistoryOptimizedModal,
      content: (
        <>
          <PublishHistory
            modalVersion
            requestInfos={() =>
              getPublishHistoryRequest({
                environmentUuid: id,
              })
            }
            reopenModal={() => handleOptimizedHistoryPublish(id)}
          />
        </>
      ),
      maxWidth: 'xl',
      footer: (
        <Grid>
          <Button id="close-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleOptimizedPublish = (
    ids: string[],
    inlineAction?: boolean,
    environmentUuid?: string
  ) => {
    modalOpen({
      title: i18n.t('PUBLISH_NEW_IMAGE_OPTIMIZED_KEY'),
      overrideClasses: overrideOptimizedModal,
      content: (
        <>
          <OptimizedPublication
            handleOptimizedPublish={handleOptimizedPublish}
            modalVersion
            partnerUuid={id}
            instanceIds={ids}
            reopenModal={() => {
              if (environmentUuid) {
                handleOptimizedHistoryPublish(environmentUuid);
              }
            }}
            inlineAction={inlineAction}
            environmentUuid={environmentUuid}
          />
          {!inlineAction && (
            <Alert
              className={classes.alertModal}
              severity="warning"
              color="info"
            >
              <Typography className={classes.alertModalText}>
                {i18n.t('MODAL_ACTIONS_BLOCKED_KEY')}
              </Typography>
            </Alert>
          )}
        </>
      ),
      maxWidth: 'md',
    });
  };

  const handleImagesLogs = (item: PartnerServersFields) =>
    modalOpen({
      title: i18n.t('ENVIRONMENTS_PAGE.LOGS_PUBLICATION_KEY'),
      content: (
        <ImagesLogs
          onCancel={modalClose}
          id={item?.environment?.uuid}
          instanceId={item?.instance_id}
          query={query}
        />
      ),

      maxWidth: 'lg',
    });

  const handleNewScheduleStop = (item: PartnerServersFields) =>
    modalOpen({
      title: i18n.t('NEW_SCHEDULED_STOP_KEY'),
      subtitle: i18n.t('NEW_SCHEDULED_STOP_SUBTITLE_KEY'),
      content: (
        <FormNewScheduledStop
          initialValues={{
            stop_time: item?.stop_time !== 'N/A' ? item?.stop_time : new Date(),
            new_stop_time: item?.stop_time !== 'N/A' ? item?.stop_time : null,
          }}
          onSubmit={time => {
            const newStopTime = formatDateToISO(time?.new_stop_time);

            stopTimeRequest({
              environmentUuid: item?.environment?.uuid,
              serverId: item?.instance_id,
              values: {
                stop_time: newStopTime,
                disable_auto_stop: false,
              },
              query,
              partnerUuid: id,
              server_type: 'preparation',
            });
            modalClose();
          }}
          onCancel={modalClose}
        />
      ),
    });

  const handleDetachServerPreparation = (item: PartnerServersFields) =>
    modalOpen({
      title: i18n.t('ENVIRONMENTS_PAGE.DETACH_SERVER_KEY'),
      content: i18n.t('ENVIRONMENTS_PAGE.DETACH_SERVER_MSG_KEY', {
        environment: item?.environment?.name,
      }),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              detachServerRequest({
                instanceId: item?.instance_id,
                environmentUuid: item?.environment?.uuid,
                query,
                type: 'preparation',
                partnerUuid: id,
              });
              modalClose();
            }}
          >
            {i18n.t('UNLINK_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });

  const handleExtendServer = (item: PartnerServersFields) => {
    modalOpen({
      title: i18n.t('ENVIRONMENTS_PAGE.WANT_EXTEND_SERVER_KEY'),
      content: i18n.t('ENVIRONMENTS_PAGE.WANT_EXTEND_SERVER_MSG_KEY'),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              stopTimeRequest({
                environmentUuid: item?.environment?.uuid,
                serverId: item?.instance_id,
                values: {
                  stop_time: null,
                  disable_auto_stop: false,
                },
                query,
                partnerUuid: id,
                server_type: 'preparation',
              });
              modalClose();
            }}
          >
            {i18n.t('EXTEND_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleCancelShutdown = (item: PartnerServersFields) => {
    modalOpen({
      title: i18n.t('ENVIRONMENTS_PAGE.WANT_REMOVE_SHUTDOWN_TITLE_KEY'),
      content: (
        <Typography className={classes.messageStopServer}>
          {i18n.t('ENVIRONMENTS_PAGE.WANT_REMOVE_SHUTDOWN_MSG_KEY')}{' '}
          {formatDateWithMoment(item?.stop_time)}
        </Typography>
      ),
      footer: (
        <Grid>
          <Button
            submitButton
            id="submit-button"
            onClick={() => {
              stopTimeRequest({
                environmentUuid: item?.environment?.uuid,
                serverId: item?.instance_id,
                values: {
                  stop_time: null,
                  disable_auto_stop: true,
                },
                query,
                partnerUuid: id,
                server_type: 'preparation',
              });
              modalClose();
            }}
          >
            {i18n.t('REMOVE_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleBulkStartServers = () => {
    modalOpen({
      title: i18n.t('START_SERVERS_KEY'),
      maxWidth: 'md',
      content: (
        <>
          <Typography>{i18n.t('START_SERVERS_CONFIRM_KEY')}</Typography>
          <Alert className={classes.alertModal} severity="warning" color="info">
            <Typography className={classes.alertModalText}>
              {i18n.t('MODAL_ACTIONS_BLOCKED_KEY')}
            </Typography>
          </Alert>
        </>
      ),
      footer: (
        <Grid>
          <Button
            submitButton
            id="start-partner-servers"
            onClick={() => {
              startPartnerServersRequest({
                id,
                instance_ids: checks,
                query,
                server_type: 'preparation',
              });
              modalClose();
            }}
          >
            {i18n.t('START_SERVERS_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const handleBulkStopServers = () => {
    modalOpen({
      title: i18n.t('STOP_SERVERS_KEY'),
      maxWidth: 'md',
      content: (
        <>
          <Typography>{i18n.t('STOP_SERVERS_CONFIRM_KEY')}</Typography>
          <Alert className={classes.alertModal} severity="warning" color="info">
            <Typography className={classes.alertModalText}>
              {i18n.t('MODAL_ACTIONS_BLOCKED_KEY')}
            </Typography>
          </Alert>
        </>
      ),
      footer: (
        <Grid>
          <Button
            submitButton
            id="stop-partner-servers"
            onClick={() => {
              stopPartnerServersRequest({
                id,
                instance_ids: checks,
                query,
                server_type: 'preparation',
              });
              modalClose();
            }}
          >
            {i18n.t('STOP_SERVERS_KEY')}
          </Button>
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CANCEL_KEY')}
          </Button>
        </Grid>
      ),
    });
  };

  const headers: Array<TableCellHeaderProps & { hidden?: boolean }> = [
    {
      key: 'instance_id',
      i18n: 'ID',
      order: true,
      hidden: isClientOperator(role),
    },
    {
      key: 'name',
      i18n: 'NAME_KEY',
      order: true,
    },
    {
      key: 'environment_name',
      order: true,
      i18n: 'ENVIRONMENT_KEY',
    },
    {
      key: 'stop_time',
      i18n: 'SCHEDULED_STOP_KEY',
      order: true,
      centerTitle: true,
    },
    {
      key: 'public_ip',
      order: true,
      i18n: 'IP',
    },
    {
      key: 'flavor',
      i18n: 'FLAVOR_KEY',
      centerTitle: true,
      order: true,
      hidden: isClientOperator(role),
    },
    {
      key: 'instance_state',
      order: true,
      i18n: 'ENVIRONMENTS_PAGE.SERVER_STATUS_KEY',
    },
    {
      key: 'image_publication_status',
      order: true,
      i18n: 'ENVIRONMENTS_PAGE.IMAGE_STATUS_KEY',
    },
    {
      key: '',
      i18n: 'ACTIONS_KEY',
      centerTitle: true,
      hidden:
        !!permissions &&
        requiredActionsPermissions.every(
          permission => !permissions[permission]
        ),
    },
  ].filter(header => !header.hidden);

  const handleSubmitBlockServer = (instanceId, values) => {
    blockServerPartnerActionsRequest({
      partnerUuid: id,
      instanceId: [instanceId],
      values,
      query,
    });
    modalClose();
  };

  const handleSubmitUnblockServer = (instanceId, values) => {
    unblockServerPartnerActionsRequest({
      partnerUuid: id,
      instanceId: [instanceId],
      values,
      query,
    });
    modalClose();
  };

  const handleSubmitUnblockServerBulk = values => {
    unblockServerPartnerActionsRequest({
      partnerUuid: id,
      instanceId: checks,
      values,
      query,
    });
    modalClose();
  };

  const handleBlockActionsServerPreparation = (item: PartnerServersFields) =>
    modalOpen({
      title: i18n.t('BLOCK_ACTIONS_SERVER_KEY'),
      overrideClasses: overrideModalBlockActions,
      content: (
        <Grid container spacing={2}>
          <Grid item>
            <Typography className={classes.blockActionsDescription}>
              {i18n.t('BLOCKING_SERVER_ACTIONS_DESCRIPTION_KEY')}
            </Typography>
          </Grid>
          <Formik
            initialValues={blockServerInitialValues}
            onSubmit={values =>
              handleSubmitBlockServer(item.instance_id, values)
            }
            enableReinitialize
            validationSchema={blockSchema}
          >
            {({ setFieldValue, errors, values, submitForm }) => (
              <Grid
                item
                container
                xs={12}
                spacing={2}
                className={classes.reasonField}
              >
                <GenericForm
                  fields={values}
                  screenFields={[
                    {
                      gridXs: 12,
                      label: i18n.t('BLOCKING_TIME_SERVER_KEY'),
                      stateKey: 'block_actions_time_duration',
                      isSelect: true,
                      options: [
                        { label: `30 ${i18n.t('MINUTES_KEY')}`, value: 30 },
                        {
                          label: `60 ${i18n.t('MINUTES_KEY')}`,
                          value: 60,
                        },
                        {
                          label: `90 ${i18n.t('MINUTES_KEY')}`,
                          value: 90,
                        },
                      ],
                    },
                  ]}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                <GenericForm
                  fields={values}
                  screenFields={[
                    {
                      gridXs: 12,
                      label: i18n.t('BLOCKING_REASON_KEY'),
                      stateKey: 'block_reason',
                      isMultiline: true,
                    },
                  ]}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                <Grid
                  container
                  justify="flex-end"
                  className={classes.buttonsBlockServerActions}
                >
                  <Button id="submit-button" submitButton onClick={submitForm}>
                    {i18n.t('BLOCK_KEY')}
                  </Button>
                  <Button
                    id="cancel-button"
                    variant="outlined"
                    onClick={modalClose}
                  >
                    {i18n.t('CANCEL_KEY')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Formik>
        </Grid>
      ),
    });

  const handleUnblockActionsServerPreparation = (item?: PartnerServersFields) =>
    modalOpen({
      title: i18n.t('UNBLOCK_ACTIONS_SERVER_KEY'),
      overrideClasses: overrideModalBlockActions,
      content: (
        <Grid container spacing={2}>
          {item && (
            <Grid item>
              {!isClientOperator(role) ? (
                <>
                  <Typography className={classes.alertText}>
                    {i18n.t('UNBLOCKING_SERVER_ACTIONS_RESPONSIBLE_KEY', {
                      name: `${item?.blocked_by?.first_name} ${item?.blocked_by?.last_name} (${item?.blocked_by?.email})`,
                      time: formatHourMinute(item?.blocked_at),
                      unblockDate: formatHourMinute(item?.unblock_time),
                    })}
                  </Typography>
                  <Typography className={classes.alertText} component="div">
                    <Box
                      color={palette.blue}
                      fontWeight="bold"
                      display="inline"
                    >
                      {`${i18n.t('BLOCKING_REASON_KEY')}: `}
                    </Box>
                    {item?.block_reason}
                  </Typography>
                </>
              ) : (
                <Typography className={classes.alertText}>
                  {i18n.t('SERVER_MAINTENANCE_ACTIONS_RELEASE_KEY', {
                    unblockDate: formatHourMinute(item?.unblock_time),
                  })}
                </Typography>
              )}
              <Typography className={classes.alertText}>
                {i18n.t('UNBLOCKING_SERVER_ACTIONS_DESCRIPTION_KEY')}
              </Typography>
            </Grid>
          )}
          <Formik
            initialValues={unblockServerInitialValues}
            onSubmit={values => {
              if (item) {
                handleSubmitUnblockServer(item.instance_id, values);
              } else {
                handleSubmitUnblockServerBulk(values);
              }
            }}
            enableReinitialize
            validationSchema={unblockSchema}
          >
            {({ setFieldValue, errors, values, submitForm }) => (
              <Grid
                item
                container
                xs={12}
                spacing={2}
                className={classes.reasonField}
              >
                <GenericForm
                  fields={values}
                  screenFields={[
                    {
                      gridXs: 12,
                      label: i18n.t('UNBLOCKING_REASON_KEY'),
                      stateKey: 'unblock_reason',
                    },
                  ]}
                  setFieldValue={setFieldValue}
                  errors={errors}
                />
                <Grid
                  container
                  justify="flex-end"
                  className={classes.buttonsBlockServerActions}
                >
                  <Button id="submit-button" submitButton onClick={submitForm}>
                    {i18n.t('UNBLOCK_KEY')}
                  </Button>
                  <Button
                    id="cancel-button"
                    variant="outlined"
                    onClick={modalClose}
                  >
                    {i18n.t('CANCEL_KEY')}
                  </Button>
                </Grid>
              </Grid>
            )}
          </Formik>
        </Grid>
      ),
    });

  const getGeneralActionsItems = () => {
    const menuGeneralActionsItems = [
      {
        key: 'btn-bulk-start-server',
        label: i18n.t('START_SERVERS_KEY'),
        onClick: () => handleBulkStartServers(),
        disabled: checks.length === 0,
        hidden: !permissions?.start_partner_servers,
      },
      {
        key: 'btn-bulk-stop-server',
        label: i18n.t('STOP_SERVERS_KEY'),
        onClick: () => handleBulkStopServers(),
        disabled: checks.length === 0,
        hidden: !permissions?.stop_partner_servers,
      },
      {
        key: 'btn-bulk-optimized-publish',
        label: i18n.t('PUBLISH_NEW_IMAGE_OPTIMIZED_KEY'),
        onClick: () => {
          handleOptimizedPublish(checks);
        },
        disabled: checks.length === 0,
        hidden: !permissions?.publish_image_partner_servers,
        style: {
          borderTop: '1px solid #EEE',
        },
      },
      {
        key: 'btn-bulk-unblock-actions',
        label: i18n.t('UNBLOCKING_ACTIONS_KEY'),
        icon: <LockOpenIcon className={classes.iconLock} />,
        disabled: checks.length === 0,
        hidden: !permissions?.can_unblock_actions_partner_servers,
        onClick: () => handleUnblockActionsServerPreparation(),
      },
    ];

    return menuGeneralActionsItems.filter(action => !action.hidden);
  };

  const handleOpenBlockDetails = (item: PartnerServersFields) =>
    modalOpen({
      title: i18n.t('BLOCKED_ACTIONS_PARTNER_KEY'),
      overrideClasses: overrideModalBlockActions,
      content: (
        <Grid item>
          {!isClientOperator(role) ? (
            <Grid item>
              <Typography className={classes.alertText}>
                {i18n.t('BLOCKED_ACTIONS_ALERT_DESCRIPTION_KEY', {
                  name: `${item?.blocked_by?.first_name} ${item?.blocked_by?.last_name} (${item?.blocked_by?.email})`,
                  time: formatHourMinute(item?.blocked_at),
                })}
              </Typography>
              <Typography className={classes.alertText} component="div">
                <Box color={palette.blue} fontWeight="bold" display="inline">
                  {`${i18n.t('BLOCKING_REASON_KEY')}: `}
                </Box>
                {item?.block_reason}
              </Typography>
              <Grid container justify="space-between">
                <Grid item>
                  <Typography className={classes.alertText}>
                    {i18n.t('BLOCKED_ACTIONS_RELEASE_KEY', {
                      unblockDate: formatHourMinute(item?.unblock_time),
                    })}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
          ) : (
            <Typography className={classes.alertText}>
              {i18n.t('SERVER_MAINTENANCE_ACTIONS_RELEASE_KEY', {
                unblockDate: formatHourMinute(item?.unblock_time),
              })}
            </Typography>
          )}
        </Grid>
      ),
      footer: (
        <Grid>
          {permissions?.can_unblock_actions_partner_servers && (
            <Button
              variant="outlined"
              outlineBlue
              onClick={() => handleUnblockActionsServerPreparation(item)}
            >
              {i18n.t('UNBLOCK_KEY')}
            </Button>
          )}
          <Button id="cancel-button" variant="outlined" onClick={modalClose}>
            {i18n.t('CLOSE_KEY')}
          </Button>
        </Grid>
      ),
    });

  const getMenuActionItems = (
    item: PartnerServersFields
  ): MenuActionItemProps[] => {
    const menuActionItems: MenuActionItemProps[] = [
      {
        key: 'btn-blocked-actions-info',
        label: i18n.t('BLOCKED_ACTIONS_PARTNER_KEY'),
        onClick: () => handleOpenBlockDetails(item),
        hidden: !item?.block_actions_enabled,
        infoButton: true,
        style: {
          backgroundColor: palette.backgroundBlue,
        },
        icon: <InfoIcon className={classes.iconLock} />,
      },
      {
        key: 'btn-start-server',
        label: i18n.t('ENVIRONMENTS_PAGE.START_SERVER_KEY'),
        onClick: () =>
          handleStartServer(item, item?.environment?.uuid, id, 'preparation'),
        hidden:
          item?.instance_state !== InstanceStateEnum.STOPPED ||
          !permissions?.start_preparation_server,
        disabled: isActionsBlockedForOperator(item),
      },
      {
        key: 'btn-stop-server',
        label: i18n.t('ENVIRONMENTS_PAGE.STOP_SERVER_KEY'),
        onClick: () =>
          handleStopServer(item, item?.environment?.uuid, id, 'preparation'),
        hidden:
          item?.instance_state !== InstanceStateEnum.RUNNING ||
          !permissions?.stop_preparation_server,
        disabled: isActionsBlockedForOperator(item),
      },
      {
        key: 'btn-extend-cancel-execution',
        label: i18n.t('EXTEND_EXECUTION_KEY'),
        onClick: () => handleExtendServer(item),
        hidden:
          (!!item?.stop_time && item?.stop_time === 'N/A') ||
          !permissions?.stop_preparation_server,
        disabled: isActionsBlockedForOperator(item),
      },
      {
        key: 'btn-cancel-scheduled-stop',
        label: i18n.t('CANCEL_SCHEDULED_STOP_KEY'),
        onClick: () => handleCancelShutdown(item),
        hidden:
          (!!item?.stop_time && item?.stop_time === 'N/A') ||
          !permissions?.stop_preparation_server,
        disabled: isActionsBlockedForOperator(item),
      },
      {
        key: 'btn-unlink',
        label: i18n.t('UNLINK_KEY'),
        onClick: () => handleDetachServerPreparation(item),
        hidden: !permissions?.detach_preparation_server,
        disabled: isActionsBlockedForOperator(item),
      },
      {
        key: 'btn-scheduled-stop',
        label: i18n.t('NEW_SCHEDULED_STOP_KEY'),
        onClick: () => handleNewScheduleStop(item),
        hidden: !permissions?.stop_preparation_server,
        disabled: isActionsBlockedForOperator(item),
      },
      {
        key: 'btn-list-logs',
        label: i18n.t('ENVIRONMENTS_PAGE.LOGS_PUBLICATION_KEY'),
        onClick: () => handleImagesLogs(item),
        hidden: !permissions?.publish_image,
      },
      {
        key: 'btn-optimized-publish',
        label: i18n.t('PUBLISH_NEW_IMAGE_OPTIMIZED_KEY'),
        onClick: () => {
          handleOptimizedPublish(
            [item?.instance_id],
            true,
            item.environment.uuid
          );
        },
        hidden: !permissions?.publish_image_partner_servers,
        style: {
          borderTop: '1px solid #EEE',
        },
        disabled: isActionsBlockedForOperator(item),
      },
      {
        key: 'btn-optimized-publish-history',
        label: i18n.t('HISTORY_LOGS_PUBLISH_OPTIMIZED_KEY'),
        onClick: () => {
          handleOptimizedHistoryPublish(item?.environment.uuid);
        },
        hidden: !permissions?.publish_image_partner_servers,
      },
      {
        key: 'block-actions',
        label: i18n.t('BLOCKING_ACTIONS_KEY'),
        icon: <LockIcon className={classes.iconLock} />,
        hidden:
          !permissions?.can_block_actions_partner_servers ||
          item?.block_actions_enabled,
        onClick: () => handleBlockActionsServerPreparation(item),
      },
      {
        key: 'unblock-actions',
        label: i18n.t('UNBLOCKING_ACTIONS_KEY'),
        icon: <LockOpenIcon className={classes.iconLock} />,
        hidden:
          !permissions?.can_unblock_actions_partner_servers ||
          !item?.block_actions_enabled,
        onClick: () => handleUnblockActionsServerPreparation(item),
      },
    ];

    return menuActionItems.filter(action => !action.hidden);
  };

  const filterValues = React.useMemo(() => {
    return filters?.reduce(
      (acc, { key, value }) => ({ ...acc, [key]: value || '' }),
      {} as { [key: string]: string }
    );
  }, [filters]);

  React.useEffect(() => {
    setQuery(prev => ({ ...prev, ...filterValues }));
  }, [filterValues, setQuery]);

  const getServers = (refresh = false) => {
    getAllPartnerServersRequest({
      query,
      id,
      server_type: 'preparation',
    });
    setRefresh(refresh);
  };

  React.useEffect(() => {
    getServers();
  }, [id, query]);

  React.useEffect(() => {
    const stop = allPartnerServersList?.results?.every(
      server =>
        isFinalizingState(server?.instance_state) &&
        isFinalizingState(server?.image_publication_status)
    );

    setStopRequest(allPartnerServersList?.count ? stop : true);
  }, [allPartnerServersList]);

  React.useEffect(() => {
    if (stopRequest) {
      clearInterval(intervalRef.current);
      setRefresh(false);
    } else {
      intervalRef.current = setInterval(() => {
        getServers(true);
      }, RELOAD_TIME);
    }

    return () => clearInterval(intervalRef.current);
  }, [stopRequest]);

  React.useEffect(() => {
    setFilterValues(filterOptions(isClientOperator(role)));
  }, [role]);

  return (
    <>
      <Grid container>
        <PartnersTemplate
          current="servers"
          title={i18n.t('SERVERS_KEY')}
          subtitle={i18n.t('LIST_ALL_PARTNER_SERVERS_KEY')}
          partnerName={partner?.name}
          breadcrumbs={[
            {
              name: i18n.t('PARTNERS_KEY'),
              link: '/partners',
            },
            {
              name: i18n.t('SERVERS_KEY'),
            },
          ]}
        >
          <Grid container justify="space-between" alignItems="center">
            <Grid container>
              <DynamicFilter
                itemsCountFiltered={allPartnerServersList?.count}
                setQuery={setQuery}
                isLoading={isLoading || isLoadingEnvironments}
                searchValue={searchValue}
                setSearchValue={setSearchValue}
                handleSearch={handleSearch}
              />
            </Grid>
            <Grid
              container
              justify="flex-end"
              alignItems="center"
              className={classes.actions}
            >
              <RefreshButton
                id="btn-refresh"
                onClick={() => getServers(true)}
                animate={isLoading || isLoadingEnvironments}
                disabled={isLoading || isLoadingEnvironments}
              />

              <Permission value={requiredBulkActionsPermissions}>
                <ButtonGroup
                  label={i18n.t('GENERAL_ACTIONS_KEY')}
                  options={getGeneralActionsItems()}
                />
              </Permission>
            </Grid>
            <TableItems
              header={
                <>
                  <Permission value={requiredBulkActionsPermissions}>
                    <TableCell className={classes.checkboxCell}>
                      <Checkbox
                        onChange={handleAllCheck}
                        id="select-all-partner-servers"
                      />
                    </TableCell>
                  </Permission>
                  {headers.map(header => (
                    <TableCell
                      key={header.key}
                      centerTitle={header.centerTitle}
                      order={header?.order}
                      markOderItem={itemOrder === header?.key}
                      handleOrder={() =>
                        header?.order ? handleOrder(header?.key) : null
                      }
                    >
                      {i18n.t(header?.i18n)}
                    </TableCell>
                  ))}
                </>
              }
              renderItems={(item: PartnerServersFields, index) =>
                item?.server_type === 'preparation' && (
                  <TableRow key={item?.instance_id}>
                    <Permission value={requiredBulkActionsPermissions}>
                      {getGeneralActionsItems()?.length > 0 && (
                        <TableCell className={classes.checkboxCell}>
                          <Checkbox
                            onChange={() => handleCheck(item?.instance_id)}
                            checked={
                              !!checks.find(
                                check => check === item?.instance_id
                              )
                            }
                          />
                        </TableCell>
                      )}
                    </Permission>
                    {!isClientOperator(role) && (
                      <TableCell ellipsis copyable maxLength={20}>
                        {item?.instance_id}
                      </TableCell>
                    )}
                    <TableCell
                      copyable
                      maxLength={20}
                      ellipsis
                      tooltip={item?.name}
                    >
                      {item?.name}
                    </TableCell>
                    <TableCell
                      copyable
                      maxLength={20}
                      ellipsis
                      className={classes.longField}
                      tooltip={item?.environment.name}
                    >
                      {item?.environment.name}
                    </TableCell>
                    <TableCell
                      className={classes.longField}
                      align="center"
                      tooltip={
                        item?.stop_time === 'N/A'
                          ? i18n.t('ENVIRONMENTS_PAGE.NOT_STOP_TIME_KEY')
                          : ''
                      }
                    >
                      <>
                        {item?.stop_time && item?.stop_time !== 'N/A' ? (
                          formatDateWithMoment(item?.stop_time)
                        ) : (
                          <AlarmOffIcon className={classes.alarmIcon} />
                        )}
                      </>
                    </TableCell>
                    <TableCell copyable tooltip={item?.public_ip}>
                      <Grid container>{item?.public_ip}</Grid>
                    </TableCell>
                    {!isClientOperator(role) && (
                      <TableCell>
                        <span className={classes.flag}>
                          {getFlavorDisplayName(item?.flavor)}

                          {getFlavorDisplayName(item?.flavor)}
                        </span>
                      </TableCell>
                    )}
                    <TableCell
                      align="center"
                      tooltip={getStatusTextServer(item?.instance_state)}
                    >
                      <Grid container>
                        <StatusInstanceServer value={item?.instance_state} />
                      </Grid>
                    </TableCell>
                    <TableCell
                      align="center"
                      tooltip={getStatusText(item?.image_publication_status)}
                      className={classes.imageCell}
                    >
                      {isFinalizingState(item?.image_publication_status) ? (
                        <Grid container justify="center">
                          <StatusInstance
                            value={item?.image_publication_status}
                          />
                        </Grid>
                      ) : (
                        <Grid container alignItems="center" justify="center">
                          <CircularProgress
                            className={classes.iconReload}
                            size={15}
                            color="secondary"
                          />
                          <Typography className={classes.statusText}>
                            {getStatusText(item?.image_publication_status)}
                          </Typography>
                        </Grid>
                      )}
                    </TableCell>
                    <Permission value={requiredActionsPermissions}>
                      <TableCell textAlignCenter>
                        <Grid className={classes.menuActionsAndLock}>
                          <MenuActions
                            id={`btn-options-partner-servers-${index}`}
                          >
                            {getMenuActionItems(item)?.length > 0 ? (
                              getMenuActionItems(item)?.map(action => (
                                <MenuActionItem
                                  key={action?.key}
                                  id={action?.id ?? action?.key}
                                  onClick={action?.onClick}
                                  style={action?.style}
                                  disabled={action?.disabled}
                                >
                                  {action?.label}
                                  {action?.icon}
                                </MenuActionItem>
                              ))
                            ) : (
                              <MenuActionItem>
                                {i18n.t('NO_ACTIONS_AVAILABLE_KEY')}
                              </MenuActionItem>
                            )}
                          </MenuActions>
                          {item.block_actions_enabled && (
                            <Tooltip
                              arrow
                              title={
                                permissions?.can_unblock_actions_partner_servers
                                  ? i18n.t('UNBLOCK_ACTIONS_TOOLTIP_SERVER_KEY')
                                  : i18n.t('BLOCKED_ACTIONS_PARTNER_KEY')
                              }
                            >
                              <LockIcon
                                className={classes.iconLock}
                                onClick={() => {
                                  if (
                                    permissions?.can_unblock_actions_partner_servers
                                  ) {
                                    handleUnblockActionsServerPreparation(item);
                                  } else {
                                    handleOpenBlockDetails(item);
                                  }
                                }}
                              />
                            </Tooltip>
                          )}
                        </Grid>
                      </TableCell>
                    </Permission>
                  </TableRow>
                )
              }
              data={allPartnerServersList}
              isLoading={!refresh && (isLoading || isLoadingEnvironments)}
              notFoundText={i18n.t(
                'ENVIRONMENTS_PAGE.NO_PREPARATION_SERVER_KEY'
              )}
              onChangePage={handlePageChange}
              onChangeLimit={handleLimitChange}
            />
          </Grid>
        </PartnersTemplate>
      </Grid>
    </>
  );
};

const mapStateToProps = ({
  partners: { isLoading, allPartnerServersList, partnerDetails },
  authenticate: { data },
  filters: { filters },
  environments: { isLoading: isLoadingEnvironments },
}: ApplicationState) => ({
  isLoading,
  permissions: data?.permissions,
  role: data?.role,
  allPartnerServersList,
  filters,
  isLoadingEnvironments,
  partner: partnerDetails,
});

export default connect(mapStateToProps, {
  getAllPartnerServersRequest,
  startPartnerServersRequest,
  stopPartnerServersRequest,
  modalOpen,
  modalClose,
  detachServerRequest,
  stopTimeRequest,
  setFilterValues,
  getPublishHistoryRequest,
  blockServerPartnerActionsRequest,
  unblockServerPartnerActionsRequest,
})(PartnerServers);
